<template>
  <div>
    <!-- TICKET FLOW -->
    <template v-if="tab === 'requirements'">
      <!-- Filters NARROW the rows; the split selector controls stacking. They
           are independent by design, so "Bugs only, split by customer" works.
           Customer is deliberately absent — the board's customer pills already
           filter it and are passed in as a prop. Since those went multi-select
           the "By customer" split earns its keep: pick two tenants above and
           this stacks them against each other. -->
      <div class="flex justify-end items-center gap-2 mb-2">
        <select
          v-model="flowType"
          class="h-[30px] border border-gray-300 rounded px-2 text-xs
                 text-gray-800 bg-white"
        >
          <option :value="null">All types ({{ flow.length ? typeCounts.all : 0 }})</option>
          <option v-for="t in reqTypes" :key="t" :value="t">
            {{ t }} ({{ typeCounts[t] || 0 }})
          </option>
        </select>
        <select
          v-model="flowPriority"
          class="h-[30px] border border-gray-300 rounded px-2 text-xs
                 text-gray-800 bg-white"
        >
          <option :value="null">All priorities</option>
          <option v-for="p in priorities" :key="p" :value="p">
            {{ priorityLabel(p) }} ({{ priorityCounts[p] || 0 }})
          </option>
        </select>
        <select
          v-model.number="flowWeeks"
          class="h-[30px] border border-gray-300 rounded px-2 text-xs
                 text-gray-800 bg-white"
        >
          <option :value="4">Last 4 weeks</option>
          <option :value="8">Last 8 weeks</option>
          <option :value="0">All {{ allWeeks.length }} weeks</option>
        </select>
        <span class="w-px h-[18px] bg-gray-300" />
        <select
          v-model="flowGroup"
          class="h-[30px] border border-gray-300 rounded px-2 text-xs
                 text-gray-800 bg-white"
        >
          <option value="none">No split</option>
          <option value="priority">By priority</option>
          <option value="customer">By customer</option>
        </select>
      </div>
      <p v-if="!flow.length" class="text-xs text-gray-600 py-8">
        No ticket movement in the last 12 weeks.
      </p>
      <!-- A filter can empty the chart even when `flow` has rows. Say so,
           rather than rendering an axis with nothing on it. -->
      <p v-else-if="!filteredFlow.length" class="text-xs text-gray-600 py-8">
        No ticket movement matches these filters.
        <button type="button" class="text-primary-700 underline" @click="resetFlowFilters">
          Clear filters
        </button>
      </p>
      <Chart_V5
        v-else
        :chart_data="flowData"
        :chart_config="flowConfig"
        :show-export="false"
        override-height="h-[340px]"
      />
    </template>

    <!-- ENGINEERING WORK -->
    <template v-else>
      <div class="flex justify-end mb-2">
        <select
          v-model="engineer"
          class="h-[30px] border border-gray-300 rounded px-2 text-xs
                 text-gray-800 bg-white"
        >
          <option :value="null">All engineers</option>
          <option v-for="name in engineers" :key="name" :value="name">{{ name }}</option>
        </select>
      </div>
      <p v-if="!workData.series.length" class="text-xs text-gray-600 py-8">
        No merged pull requests to show.
      </p>
      <Chart_V5
        v-else
        :chart_data="workData"
        :chart_config="workConfig"
        :show-export="false"
        override-height="h-[340px]"
      />
    </template>
  </div>
</template>

<script>
import Chart_V5 from '@/components/widgetcomponents/Viz_Elements/Chart_V5.vue';
import {
  PRIORITY_CODES, PRIORITY_LABELS, priorityToCode,
} from '../support_constants';
import { CATEGORY_LABELS, label } from './hades_constants';

// Colours are chart-specific and stay local; the codes, the ordering and the
// English come from the shared vocabulary. priorityToCode() is the same fold
// the old local priorityToCode() did, plus it maps an unrecognised value
// onto 'none' instead of leaving it as its own phantom series.
const PRIORITY_COLORS = {
  p0: '#D53D3D',
  p1: '#F76734',
  p2: '#F1BD6C',
  p3: '#88A484',
  none: '#D2D2D3',
};
const GROUP_COLORS = ['#F76734', '#4C6E97', '#88A484', '#F1BD6C', '#8E8F90', '#B7ACFC'];

const CATEGORY_COLORS = {
  customer_feature: '#F76734',
  bug_fix: '#D53D3D',
  app_stability_reliability: '#4C6E97',
  architecture_platform: '#88A484',
  data_integration: '#F1BD6C',
  security_compliance: '#8D84E8',
  internal_tooling: '#A5A5A6',
  documentation: '#D2D2D3',
  maintenance: '#78797A',
  unclassified: '#E8E9E9',
};

export default {
  name: 'Hades_Charts',
  components: { Chart_V5 },
  props: {
    // Which chart to render. The mockup shows ONE chart at a time, matching the
    // active tab, rather than stacking both — the engineering chart is not
    // about the requirement board and vice versa.
    tab: { type: String, default: 'requirements' },
    flow: { type: Array, required: true },
    pullRequests: { type: Array, required: true },
    // The board's customer pills, as selected. EMPTY MEANS EVERY CUSTOMER —
    // not "no customer" — which is why every check below is `!length ||
    // includes`, never a bare `includes`.
    customers: { type: Array, default: () => [] },
  },
  data() {
    return {
      engineer: null,
      flowGroup: 'none',
      flowType: null,
      flowPriority: null,
      // 0 = all available weeks. Default to the full window so the chart opens
      // showing everything, as it did before filters existed.
      flowWeeks: 0,
    };
  },
  computed: {
    engineers() {
      return [...new Set(this.pullRequests
        .map((pr) => pr.engineer_id || pr.author_login)
        .filter(Boolean))].sort();
    },
    // Every week present in the payload, before any filter. The week AXIS is
    // built from this rather than from the filtered rows, so filtering to a
    // sparse slice does not silently collapse the x-axis to the few weeks that
    // happen to have movement — a week with no matching tickets is a real zero,
    // not an absent week.
    allWeeks() {
      return [...new Set(this.flow.map((row) => row.week_start))].sort();
    },
    weeks() {
      const all = this.allWeeks;
      if (!this.flowWeeks || this.flowWeeks >= all.length) return all;
      return all.slice(-this.flowWeeks);
    },
    // Rows surviving the customer prop AND the three in-chart filters.
    // Order matters only for readability; each predicate is independent.
    filteredFlow() {
      const weekSet = new Set(this.weeks);
      return this.flow.filter((row) => (
        this.admitsCustomer(row)
        && (!this.flowType || row.req_type === this.flowType)
        && (!this.flowPriority
            || priorityToCode(row.priority) === this.flowPriority)
        && weekSet.has(row.week_start)
      ));
    },
    // Options are derived from what the payload actually contains, not a
    // hardcoded list — a type or priority that never appears is not offered.
    // Counts are movement volume (in + out), which is what the chart plots.
    reqTypes() {
      return [...new Set(this.flow.map((r) => r.req_type).filter(Boolean))].sort();
    },
    priorities() {
      const present = new Set(this.flow.map((r) => priorityToCode(r.priority)));
      return PRIORITY_CODES.filter((p) => present.has(p));
    },
    // Counts respect the OTHER active filters, so each dropdown shows what
    // selecting it would actually yield rather than a context-free total.
    typeCounts() {
      const out = { all: 0 };
      this.flow.forEach((row) => {
        if (!this.admitsCustomer(row)) return;
        if (this.flowPriority
            && priorityToCode(row.priority) !== this.flowPriority) return;
        const n = row.incoming + row.outgoing;
        out.all += n;
        out[row.req_type] = (out[row.req_type] || 0) + n;
      });
      return out;
    },
    priorityCounts() {
      const out = {};
      this.flow.forEach((row) => {
        if (!this.admitsCustomer(row)) return;
        if (this.flowType && row.req_type !== this.flowType) return;
        const key = priorityToCode(row.priority);
        out[key] = (out[key] || 0) + row.incoming + row.outgoing;
      });
      return out;
    },
    // Names the SCOPE of the cumulative line. Without this a filtered chart
    // shows a line labelled "Open backlog" that is really the backlog of P1
    // bugs at one customer — the label is the only thing distinguishing them.
    backlogLabel() {
      const scope = [
        this.flowPriority ? this.priorityLabel(this.flowPriority) : null,
        this.flowType || null,
      ].filter(Boolean).join(' ');
      return scope ? `Open ${scope} backlog` : 'Open backlog (cumulative)';
    },
    flowData() {
      const rows = this.filteredFlow;

      // IN ABOVE THE LINE, OUT BELOW IT — one diverging stack per week. Closed
      // values are NEGATED and share New's offset, so the bar reads as a single
      // gesture: is the queue growing or shrinking.
      //
      // This needed a Chart_V5 fix. Its bar height was
      // `min(scale(start), innerHeight) - scale(end)`, which goes negative for a
      // negative value, and an SVG rect with negative height silently does not
      // render. Now min/abs, and the domain tracks the stack's negative extent.
      //
      // flowGroup only decides how each side is SPLIT — nothing, priority, or
      // customer. It is not a choice about in vs out.
      const perWeek = {};
      rows.forEach((row) => {
        const week = perWeek[row.week_start] || { incoming: 0, outgoing: 0 };
        week.incoming += row.incoming;
        week.outgoing += row.outgoing;
        perWeek[row.week_start] = week;
      });

      // OPENING BALANCE. The backlog is a RUNNING NET, so starting it at 0 on
      // the first VISIBLE week claims the queue was empty then. Under a
      // shortened week window that is simply false — "last 4 weeks" would draw
      // a line rising from zero when the real backlog was already at 85. So we
      // accumulate the weeks that were filtered OUT by the window (respecting
      // the customer/type/priority filters, which DO legitimately change what
      // is being counted) and open from there.
      const opening = this.flow.reduce((sum, row) => {
        if (!this.admitsCustomer(row)) return sum;
        if (this.flowType && row.req_type !== this.flowType) return sum;
        if (this.flowPriority
            && priorityToCode(row.priority) !== this.flowPriority) return sum;
        // Only weeks BEFORE the visible window contribute to the opening.
        if (row.week_start >= this.weeks[0]) return sum;
        return sum + row.incoming - row.outgoing;
      }, 0);

      let running = opening;
      const backlogSeries = {
        values: this.weeks.map((week) => {
          const w = perWeek[week] || { incoming: 0, outgoing: 0 };
          running += w.incoming - w.outgoing;
          return running;
        }),
        config: {
          // The label states the scope, because under a filter this is the
          // backlog OF THAT SLICE, not of the whole board — a cumulative line
          // is the easiest series to misread as a global total.
          label: this.backlogLabel,
          backgroundColor: '#353537',
          type: 'line',
          yAxis: 'right',
          offset: 0,
        },
      };

      if (this.flowGroup === 'none') {
        return {
          labels: this.weeks.map(this.weekLabel),
          series: [
            {
              values: this.weeks.map((w) => perWeek[w]?.incoming || 0),
              config: { label: 'Opened', backgroundColor: '#F76734',
                        type: 'bar', yAxis: 'left', offset: 0 },
            },
            {
              // NEGATED: closed tickets grow DOWNWARD from zero. Same offset as
              // Opened, so one bar per week reads in above the line, out below.
              // Two colours here because there is no group to colour by.
              values: this.weeks.map((w) => -(perWeek[w]?.outgoing || 0)),
              config: { label: 'Closed', backgroundColor: '#4C6E97',
                        type: 'bar', yAxis: 'left', offset: 0 },
            },
            backlogSeries,
          ],
        };
      }

      // Grouped: BOTH bars stack by the group. Same offsets, so New and Closed
      // stay side by side and each becomes a stack of its own.
      const byPriority = this.flowGroup === 'priority';
      const keyOf = (row) => (byPriority
        ? priorityToCode(row.priority) : row.customer);
      const at = {};
      rows.forEach((row) => {
        const key = `${row.week_start}|${keyOf(row)}`;
        const cell = at[key] || { incoming: 0, outgoing: 0 };
        cell.incoming += row.incoming;
        cell.outgoing += row.outgoing;
        at[key] = cell;
      });

      const present = new Set(rows.map(keyOf));
      const groups = byPriority
        ? PRIORITY_CODES.filter((k) => present.has(k))
        : [...present].sort();
      const groupLabel = (key) => (byPriority
        ? (PRIORITY_LABELS[key] || key) : key);
      const groupColor = (key, i) => (byPriority
        ? (PRIORITY_COLORS[key] || '#D2D2D3')
        : GROUP_COLORS[i % GROUP_COLORS.length]);

      // ONE LEGEND ENTRY PER GROUP, not two. The bar's position already says
      // whether it is new (up) or closed (down) — repeating that in the label
      // gives ten entries where five carry the information. Each group emits an
      // up series and a down series sharing a label and a colour.
      const series = [];
      groups.forEach((group, i) => {
        const name = groupLabel(group);
        const colour = groupColor(group, i);
        series.push({
          values: this.weeks.map((w) => at[`${w}|${group}`]?.incoming || 0),
          config: { label: name, backgroundColor: colour,
                    type: 'bar', yAxis: 'left', offset: 0 },
        });
        series.push({
          // Chart_V5 keys data rows by label, so the closed half needs its own
          // label — but hideFromLegend keeps it out of the legend, because one
          // group is one entry.
          values: this.weeks.map((w) => -(at[`${w}|${group}`]?.outgoing || 0)),
          config: { label: `${name} closed`, backgroundColor: colour,
                    type: 'bar', yAxis: 'left', offset: 0,
                    hideFromLegend: true },
        });
      });
      series.push(backlogSeries);

      return { labels: this.weeks.map(this.weekLabel), series };
    },
    // A round floor just under the backlog's own minimum. Derived, not fixed:
    // a hardcoded floor becomes wrong the moment the backlog drops below it,
    // and a floor ABOVE the minimum CLIPS the line off the chart — which is
    // exactly what a `Math.max(0, …)` clamp here used to do. The cumulative
    // backlog is a running net and genuinely goes NEGATIVE in weeks where more
    // was closed than opened (measured: 3 of 12 weeks sat at -8 to -18), so
    // clamping the floor to 0 silently dropped those points off the bottom
    // while the line still drew between their neighbours. Never clamp a
    // derived axis bound at zero unless the series itself cannot be negative.
    backlogFloor() {
      const values = this.flowData.series
        .filter((s) => s.config.type === 'line')
        .flatMap((s) => s.values)
        .filter((v) => v !== null && !Number.isNaN(v));
      if (!values.length) return 0;
      const lo = Math.min(...values);
      // Round down to a 25 so the ticks are readable. Math.floor on a negative
      // already rounds away from zero, so this widens the domain either way.
      return Math.floor((lo * 1.2) / 25) * 25;
    },
    flowConfig() {
      return {
        chartType: 'vertical_bar',
        margin: { top: 40, right: 55, bottom: 50, left: 50 },
        xAxis: { labelRotation: 0, fontSize: 10 },
        yAxes: [
          // ABS ticks, or the outflow half reads as negative work.
          { position: 'left', fontSize: 10, tickFormat: (v) => Math.abs(v) },
          // FITTED TO THE BACKLOG'S OWN RANGE, including its negative weeks.
          // A 0-anchored right axis clipped the weeks where the running net is
          // below zero. Fitting also lifts the line clear of the bars on most
          // weeks — but NOT all: the backlog sweeps THROUGH the bar range
          // mid-series, so no pair of linear domains fully separates them
          // (measured 1 residual overlap of 12, down from 2 clipped-and-
          // overlapping). See app_ui/data_visualization: when two series cross,
          // a second axis is the wrong tool and the answer is a separate strip.
          {
            position: 'right',
            fontSize: 10,
            min: this.backlogFloor,
          },
        ],
        legend: { display: true, position: 'right', spacing: 10 },
        // ALWAYS OFF. On a stacked bar the segment label and the bar height are
        // different numbers, so the labels overlap each other and contradict the
        // axis. The axis carries the total; the tooltip carries the split.
        labelDisplayConfig: { hideDataLabels: true },
        formatting: { percentage: false, sig_figs: 0 },
      };
    },
    workData() {
      // MERGED PER WEEK, not "open on a given day".
      //
      // The previous version counted a PR as active on every day between its
      // open and merge dates. That cannot work on this data: HadesPR.created_at
      // is the classification timestamp, and 335 of 357 rows have it AFTER
      // merged_at — so the window `opened <= day <= closed` was inverted and
      // matched zero days, leaving the chart blank.
      //
      // merged_at is a real GitHub date on 335 rows, so bucket by that. It also
      // answers a cleaner question: what shipped, and when.
      const mine = this.pullRequests.filter((pr) => pr.merged_at
        && (!this.engineer
            || (pr.engineer_id || pr.author_login) === this.engineer));
      if (!mine.length) return { labels: [], series: [] };

      const buckets = {};
      mine.forEach((pr) => {
        const week = this.weekStart(pr.merged_at);
        if (!week) return;
        buckets[week] = buckets[week] || {};
        const category = pr.primary_category || 'unclassified';
        buckets[week][category] = (buckets[week][category] || 0) + 1;
      });

      const weeks = Object.keys(buckets).sort();
      const categories = [...new Set(mine.map(
        (pr) => pr.primary_category || 'unclassified'))].sort();

      return {
        labels: weeks.map(this.weekLabel),
        // Same offset on every series = one stacked bar per week.
        series: categories.map((category) => ({
          values: weeks.map((week) => buckets[week][category] || 0),
          config: {
            label: label(CATEGORY_LABELS, category),
            backgroundColor: CATEGORY_COLORS[category] || '#D2D2D3',
            type: 'bar',
            yAxis: 'left',
            offset: 0,
          },
        })),
      };
    },
    workConfig() {
      return {
        chartType: 'vertical_bar',
        margin: { top: 40, right: 20, bottom: 50, left: 50 },
        xAxis: { labelRotation: 0, fontSize: 10 },
        yAxes: [{ position: 'left', fontSize: 10 }],
        legend: { display: true, position: 'top', spacing: 10 },
        // Stacked: the segment label and the bar height are different numbers,
        // so the axis carries the total and the tooltip carries the split.
        labelDisplayConfig: { hideDataLabels: true },
        formatting: { percentage: false, sig_figs: 0 },
      };
    },
  },
  methods: {
    weekStart(iso) {
      // Returns null on anything that is not a real date. A truthiness check on
      // merged_at is not enough — any non-empty string passes it, and
      // toISOString then throws RangeError and takes the whole render with it.
      const date = new Date(iso);
      if (Number.isNaN(date.getTime())) return null;
      const day = date.getUTCDay();
      date.setUTCDate(date.getUTCDate() - ((day + 6) % 7));
      return date.toISOString().slice(0, 10);
    },
    weekLabel(iso) {
      return new Date(`${iso.slice(0, 10)}T00:00:00Z`).toLocaleDateString(undefined,
        { month: 'short', day: 'numeric' });
    },
    // ONE PREDICATE, five call sites. Each of those used to spell out
    // `this.customer && row.customer !== this.customer`, and the empty-means-all
    // rule now has to be stated identically in all of them or one chart series
    // disagrees with another.
    admitsCustomer(row) {
      return !this.customers.length || this.customers.includes(row.customer);
    },
    priorityLabel(key) {
      return PRIORITY_LABELS[key] || key;
    },
    resetFlowFilters() {
      this.flowType = null;
      this.flowPriority = null;
      this.flowWeeks = 0;
    },
  },
};
</script>
